/**
 * Use this file for JavaScript code that you want to run in the front-end
 * on posts/pages that contain this block.
 *
 * When this file is defined as the value of the `viewScript` property
 * in `block.json` it will be enqueued on the front end of the site.
 *
 * Example:
 *
 * ```js
 * {
 *   "viewScript": "file:./view.js"
 * }
 * ```
 *
 * If you're not making any changes to this file because your project doesn't need any
 * JavaScript running in the front-end, then you should delete this file and remove
 * the `viewScript` property from `block.json`.
 *
 * @see https://developer.wordpress.org/block-editor/reference-guides/block-api/block-metadata/#view-script
 */

import { parallaxImages } from "../common/animations/parrallaxImages.js";

function ensureYoutubeAPIReady() {
	return new Promise((resolve, reject) => {
		if (window.YT && window.YT.Player) {
			return resolve();
		}

		let otherYoutubeReadyCb = null;
		if (typeof window.onYouTubeIframeAPIReady !== "undefined") {
			otherYoutubeReadyCb = window.onYouTubeIframeAPIReady;
		}

		window.onYouTubeIframeAPIReady = () => {
			otherYoutubeReadyCb && otherYoutubeReadyCb();
			resolve();
		};

		let script = document.createElement("script");
		script.id = "youtube-iframe-script";
		script.src = "https://www.youtube.com/iframe_api";
		let firstScriptTag = document.getElementsByTagName("script")[0];
		firstScriptTag.parentNode.insertBefore(script, firstScriptTag);
	});
}

function ensureVimeoAPIReady() {
	return new Promise((resolve, reject) => {
		if (window.Vimeo && window.Vimeo.Player) {
			return resolve();
		}

		let script = document.createElement("script");
		script.id = "vimeo-iframe-script";
		script.src = "https://player.vimeo.com/api/player.js";

		// Set up onload event handler
		script.onload = () => {
			// Check if the necessary objects are now available
			if (window.Vimeo && window.Vimeo.Player) {
				resolve();
			} else {
				reject(
					new Error(
						"Vimeo API script loaded, but Vimeo or Vimeo.Player is still not available",
					),
				);
			}
		};

		// Set up onerror event handler
		script.onerror = () => {
			reject(new Error("Error loading Vimeo API script"));
		};

		let firstScriptTag = document.getElementsByTagName("script")[0];
		firstScriptTag.parentNode.insertBefore(script, firstScriptTag);
	});
}

const removePosterAndOverlay = (video) => {
	const poster = video.querySelector(
		".media-and-content-video__poster",
	);
	const overlay = video.querySelector(
		".media-and-content-video__overlay",
	);

	if (poster) {
		poster.style.opacity = 0;
		poster.style.pointerEvents = "none";
	}

	if (overlay) {
		overlay.style.opacity = 0;
		overlay.style.pointerEvents = "none";
	}
};

const enterKeyPressPlay = (video, play) => {
	const playButton = video.querySelector(
		".media-and-content-video__overlay-icon",
	);
	if (playButton) {
		playButton.addEventListener("keydown", (e) => {
			if (e.key === "Enter") {
				removePosterAndOverlay(video);
				play();
			}
		});
	}
};

const handleIframeVideos = () => {
	const videoCollection = document.getElementsByClassName(
		"media-and-content-video__stacking-grid",
	);

	if (videoCollection) {
		const videoArray = Array.from(videoCollection);

		ensureYoutubeAPIReady().then(() => {
			for (let i = 0; i < videoArray.length; i++) {
				const videoItem = videoArray[i];
				// Check if the current videoItem has the class 'source-video-youtube'
				if (videoItem.dataset.videoType === "video-youtube") {
					// If the class is found, run the function and stop the loop
					videoArray.forEach((video, i) => {
						// events if youtube video is loaded
						const frame = video.getElementsByTagName("iframe");
						if (frame.length) {
							if (video.dataset.videoType === "video-youtube") {
								frame[0].style.pointerEvents = "none";

								let playAfterReady = false;
								let isPlayerReady = false;
								const player = new YT.Player(frame[0], {
									events: {
										onReady: () => {
											isPlayerReady = true;
											if (playAfterReady) {
												player.playVideo();
											}
										},
										onStateChange: (e) => {
											if (e.target.getPlayerState() === 1) {
												frame[0].style.visibility = "visible";
												frame[0].style.pointerEvents = "auto";
												removePosterAndOverlay(video);
											}
											if (
												video.dataset.loop === "true" &&
												e.target.getPlayerState() === 0
											) {
												player.playVideo();
											}
										},
									},
								});

								function handlePlay() {
									if (!isPlayerReady) {
										playAfterReady = true;
									} else {
										player.playVideo();
									}
								}

								enterKeyPressPlay(video, handlePlay);
								if (video.dataset.autoplay === "true") {
									handlePlay();
								}
								video.addEventListener("click", () => {
									handlePlay();
								});
							}
						}
					});
					break;
				}
			}
		});

		for (let i = 0; i < videoArray.length; i++) {
			const videoItem = videoArray[i];
			// Check if the current videoItem has the class 'source-video-vimeo'
			if (videoItem.dataset.videoType === "video-vimeo") {
				// If the class is found, run the function and stop the loop
				ensureVimeoAPIReady().then(() => {
					videoArray.forEach((video, i) => {
						// events if Vimeo video is loaded
						const frame = video.getElementsByTagName("iframe");

						if (frame.length) {
							if (video.dataset.videoType === "video-vimeo") {
								frame[0].onload = function () {
									ensureVimeoAPIReady().then(() => {
										const player = new Vimeo.Player(frame[0]);
										const play = () => {
											player.play();
										};

										const poster = video.querySelector(
											".media-and-content-video__poster",
										);
										const overlay = video.querySelector(
											".media-and-content-video__overlay",
										);

										if (poster) {
											poster.addEventListener("click", () => {
												play();
											});
										}

										if (overlay) {
											overlay.addEventListener("click", () => {
												play();
											});
										}

										enterKeyPressPlay(video, play);

										player.on("play", () => {
											removePosterAndOverlay(video);
										});
									});
								};
							}
						}
					});
				});
				break;
			}
		}
	}
};

const handleLocalVideos = () => {
	const fullWidthVideoBlocks = document.querySelectorAll(
		".wp-block-create-block-media-and-content",
	);

	fullWidthVideoBlocks.forEach((block) => {
		const video = block.querySelector(
			".media-and-content__local-video",
		);
		const playButton = block.querySelector(
			".media-and-content-video__overlay",
		);
		const stackingGrid = block.querySelector(
			".media-and-content-video__stacking-grid",
		);

		if (video) {
			const play = () => {
				video.play();
			};

			const poster = block.querySelector(
				".media-and-content-video__poster",
			);
			const overlay = block.querySelector(
				".media-and-content-video__overlay",
			);

			if (poster) {
				if (stackingGrid.dataset.controls === "true") {
					video.controls = false;
				}

				poster.addEventListener("click", () => {
					if (stackingGrid.dataset.controls === "true") {
						video.controls = true;
					}
					play();
				});
			}

			if (overlay) {
				overlay.addEventListener("click", () => {
					if (stackingGrid.dataset.controls === "true") {
						video.controls = true;
					}
					play();
				});
			}

			if (playButton) {
				playButton.addEventListener("keydown", (e) => {
					if (e.key === "Enter") {
						if (stackingGrid.dataset.controls === "true") {
							video.controls = true;
						}
						removePosterAndOverlay(video);
						play();
					}
				});
			}

			video.addEventListener("playing", function () {
				removePosterAndOverlay(block);
			});
		}
	});
};

// Position caption absolutely at bottom of media using Y position
const positionCaptions = () => {
	const imageContainers = document.querySelectorAll(
		".media-and-content__image",
	);
	const videoContainers = document.querySelectorAll(
		".media-and-content-video__stacking-grid",
	);

	const TABLET_MAX = 1025; // Match $tablet-max breakpoint

	const updateImageCaptionPosition = (imageContainer, isInitial = false) => {
		// Find caption - child of image container
		const caption = imageContainer.querySelector(
			".media-and-content__caption",
		);
		
		if (!caption) return;

		const img = imageContainer.querySelector("img");
		if (!img) return;

		const isMobile = window.innerWidth < TABLET_MAX;

		if (isMobile) {
			// For mobile/tablet: move caption outside container to avoid overflow hidden
			const columnOne = imageContainer.closest(
				".media-and-content__column-one",
			);
			if (!columnOne) return;

			// Always ensure caption is outside image container on mobile
			if (caption.parentElement === imageContainer) {
				// Move caption to be sibling of image container
				imageContainer.parentNode.insertBefore(caption, imageContainer.nextSibling);
			}

			// Set column-one to relative positioning if not already
			columnOne.style.position = "relative";

			// Calculate image container bottom position relative to column-one
			const containerRect = imageContainer.getBoundingClientRect();
			const columnRect = columnOne.getBoundingClientRect();
			const containerBottom = containerRect.bottom - columnRect.top;
			
			
			caption.style.position = "absolute";
			caption.style.top = `${containerBottom -10}px`;
			caption.style.left = "0";
			caption.style.width = "100%";
			caption.style.minHeight = "fit-content";
			caption.style.zIndex = "10";

			// Update column-one height to account for caption (only once, not on every update)
			if (isInitial) {
				setTimeout(() => {
					const captionRect = caption.getBoundingClientRect();
					const updatedColumnRect = columnOne.getBoundingClientRect();
					const captionBottom = captionRect.bottom - updatedColumnRect.top;
					const currentColumnHeight = columnOne.offsetHeight;
					if (captionBottom > currentColumnHeight) {
						columnOne.style.minHeight = `${captionBottom}px`;
					}
				}, 100);
			}
		} else {
			// For desktop: move caption outside image container to avoid overflow hidden
			const columnOneDesktop = imageContainer.closest(
				".media-and-content__column-one",
			);
			if (!columnOneDesktop) return;

			// Always ensure caption is outside image container on desktop
			if (caption.parentElement === imageContainer) {
				// Move caption to be sibling of image container
				imageContainer.parentNode.insertBefore(caption, imageContainer.nextSibling);
			}

			// Set column-one to relative positioning if not already
			columnOneDesktop.style.position = "relative";

			// Calculate image container's bottom position relative to column-one
			const containerRect = imageContainer.getBoundingClientRect();
			const columnRect = columnOneDesktop.getBoundingClientRect();
			const containerBottom = containerRect.bottom - columnRect.top;

			caption.style.position = "absolute";
			caption.style.top = `${containerBottom -10}px`;
			caption.style.left = "0";
			caption.style.width = "100%";
			caption.style.height = "fit-content";
		}

		// Fade in up animation after position is calculated
		if (isInitial && typeof gsap !== "undefined" && typeof ScrollTrigger !== "undefined" && !caption.dataset.animated) {
			// Set initial state
			gsap.set(caption, {
				opacity: 0,
				y: 30,
			});
			
			// Create fade-in-up animation with ScrollTrigger
			var tl = gsap.timeline({
				scrollTrigger: {
					trigger: caption,
					start: "top 99%",
					end: "bottom 10%",
					toggleActions: "play none none none",
				},
			});
			
			tl.to(caption, {
				duration: 1,
				opacity: 1,
				y: 0,
				ease: "power2.out",
			});
			
			// Refresh ScrollTrigger after positioning is complete
			setTimeout(() => {
				if (typeof ScrollTrigger !== "undefined") {
					ScrollTrigger.refresh();
				}
			}, isMobile ? 200 : 100);
			
			// Mark as animated to prevent re-animating
			caption.dataset.animated = "true";
		}
	};

	const updateVideoCaptionPosition = (videoStackingGrid, isInitial = false) => {
		// Find caption - sibling of video stacking grid
		const columnOne = videoStackingGrid.closest(
			".media-and-content__column-one",
		);
		if (!columnOne) return;

		const caption = columnOne.querySelector(
			".media-and-content__caption",
		);
		
		if (!caption) return;

		// Find video wrapper and video element
		const videoWrapper = videoStackingGrid.querySelector(
			".media-and-content__video-wrapper",
		);
		if (!videoWrapper) return;

		const video = videoWrapper.querySelector("video");
		const iframe = videoWrapper.querySelector("iframe");
		
		// Determine media element (video or iframe)
		const mediaElement = video || iframe;
		if (!mediaElement) return;

		// For local videos, use .media-and-content__video for positioning
		// For iframe videos (YouTube/Vimeo), use video wrapper
		const positionElement = video ? videoWrapper.querySelector(".media-and-content__video") : videoWrapper;
		if (!positionElement) return;

		const isMobile = window.innerWidth < TABLET_MAX;

		if (isMobile) {
			// For mobile/tablet: caption is already outside (sibling of stacking grid)
			// Set column-one to relative positioning if not already
			columnOne.style.position = "relative";

			// Calculate position element bottom position relative to column-one
			const positionRect = positionElement.getBoundingClientRect();
			const columnRect = columnOne.getBoundingClientRect();
			const positionBottom = positionRect.bottom - columnRect.top;
			
			caption.style.position = "absolute";
			caption.style.top = `${positionBottom + 20}px`;
			caption.style.left = "0";
			caption.style.width = "100%";
			caption.style.minHeight = "fit-content";

			// Update column-one height to account for caption (only once, not on every update)
			if (isInitial) {
				setTimeout(() => {
					const captionRect = caption.getBoundingClientRect();
					const updatedColumnRect = columnOne.getBoundingClientRect();
					const captionBottom = captionRect.bottom - updatedColumnRect.top;
					const currentColumnHeight = columnOne.offsetHeight;
					if (captionBottom > currentColumnHeight) {
						columnOne.style.minHeight = `${captionBottom}px`;
					}
				}, 100);
			}
		} else {
			// For desktop: keep caption as sibling (outside stacking grid)
			// Set column-one to relative positioning if not already
			columnOne.style.position = "relative";

			// Calculate position element's bottom position relative to column-one
			const positionRect = positionElement.getBoundingClientRect();
			const columnRect = columnOne.getBoundingClientRect();
			const positionBottom = positionRect.bottom - columnRect.top;

			caption.style.position = "absolute";
			caption.style.top = `${positionBottom + 20}px`;
			caption.style.left = "0";
			caption.style.width = "100%";
			caption.style.height = "fit-content";

			// Update column-one height to account for caption on desktop (only once, not on every update)
			// This is needed for videos since caption is outside the stacking grid
			if (isInitial) {
				setTimeout(() => {
					const captionRect = caption.getBoundingClientRect();
					const columnRect = columnOne.getBoundingClientRect();
					const captionBottom = captionRect.bottom - columnRect.top;
					const currentColumnHeight = columnOne.offsetHeight;
					if (captionBottom > currentColumnHeight) {
						columnOne.style.minHeight = `${captionBottom}px`;
					}
				}, 100);
			}
		}

		// Fade in up animation after position is calculated
		if (isInitial && typeof gsap !== "undefined" && typeof ScrollTrigger !== "undefined" && !caption.dataset.animated) {
			// Set initial state
			gsap.set(caption, {
				opacity: 0,
				y: 30,
			});
			
			// Create fade-in-up animation with ScrollTrigger
			var tl = gsap.timeline({
				scrollTrigger: {
					trigger: caption,
					start: "top 99%",
					end: "bottom 10%",
					toggleActions: "play none none none",
				},
			});
			
			tl.to(caption, {
				duration: 1,
				opacity: 1,
				y: 0,
				ease: "power2.out",
			});
			
			// Refresh ScrollTrigger after positioning is complete
			setTimeout(() => {
				if (typeof ScrollTrigger !== "undefined") {
					ScrollTrigger.refresh();
				}
			}, isMobile ? 200 : 100);
			
			// Mark as animated to prevent re-animating
			caption.dataset.animated = "true";
		}
	};

	// Initial positioning with fade-in animation
	imageContainers.forEach((container) => {
		updateImageCaptionPosition(container, true);
	});
	videoContainers.forEach((container) => {
		updateVideoCaptionPosition(container, true);
	});

	// Update on resize only (not on scroll to avoid height issues)
	const updateAllCaptions = () => {
		imageContainers.forEach((container) => {
			updateImageCaptionPosition(container);
		});
		videoContainers.forEach((container) => {
			updateVideoCaptionPosition(container);
		});
	};

	// Use ScrollTrigger to update on refresh
	if (typeof ScrollTrigger !== "undefined") {
		ScrollTrigger.addEventListener("refresh", updateAllCaptions);
	}

	// Listen to window resize with debouncing
	let resizeTimeout;
	window.addEventListener("resize", () => {
		clearTimeout(resizeTimeout);
		resizeTimeout = setTimeout(() => {
			updateAllCaptions();
		}, 150);
	});
};

// initialize gsap animations
const gsapMediaAndContentAnimations = () => {
	const wrappers = document.querySelectorAll(
		".media-and-content__image",
	);

	parallaxImages(wrappers);
};

if (document.readyState !== "loading") {
	handleIframeVideos();
	handleLocalVideos();
	gsapMediaAndContentAnimations();
	positionCaptions();
} else {
	document.addEventListener("DOMContentLoaded", handleIframeVideos);
	document.addEventListener("DOMContentLoaded", handleLocalVideos);
	document.addEventListener("DOMContentLoaded", gsapMediaAndContentAnimations);
	document.addEventListener("DOMContentLoaded", positionCaptions);
}